import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ai } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title, Button, Group } from '@repletoryreach/sdk-ui';
import { Select, Switch, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { formatCount } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';
import { AiError, ModelSelect, UnitCost, useModelsOf } from './shared';

const MAX_CHARS = 3000;

/**
 * Text to speech (14 October 2026): a text read aloud in a chosen voice, to play, download or keep in the media
 * library and send on WhatsApp. Charged per 1 000 characters.
 */
export function SpeechTab() {
  const t = useT();
  const models = useModelsOf('speech');
  const [model, setModel] = useState('');
  const chosen = models.find((m) => m.code === model) ?? models[0];
  const voicesQ = useQuery({
    queryKey: ['ai', 'voices', chosen?.code],
    queryFn: () => ai.listVoices(chosen!.code),
    enabled: !!chosen,
    staleTime: 3_600_000
  });
  const voices = voicesQ.data?.data ?? [];
  const [voice, setVoice] = useState('');
  const [text, setText] = useState('');
  const [format, setFormat] = useState<'mp3' | 'ogg'>('mp3');
  const [save, setSave] = useState(false);

  // South African English first, as the API lists them.
  useEffect(() => {
    if (voices.length && !voices.some((v) => v.id === voice)) setVoice(voices[0].id);
  }, [voices, voice]);

  const run = useMutation({
    mutationFn: () => ai.speech({ text: text.trim(), voice, model: chosen?.code ?? null, format, save })
  });

  const result = run.data?.data;
  const src = result ? `data:${result.media_type};base64,${result.audio}` : null;
  const ready = !!chosen && !!voice && !!text.trim() && text.length <= MAX_CHARS;

  return (
    <Stack gap="lg">
      <Card withBorder>
        <form onSubmit={(e) => { e.preventDefault(); if (ready) run.mutate(); }}>
          <Stack gap="md">
            <Title order={4}>{t('ai.speech.title')}</Title>
            <Textarea
              label={t('ai.speech.text')}
              description={t('ai.speech.textHint', { count: formatCount(text.length), max: formatCount(MAX_CHARS) })}
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              maxLength={MAX_CHARS}
              minRows={5}
              autosize
              required
              data-testid="ai-speech-text"
            />
            <Select
              label={t('ai.speech.voice')}
              value={voice}
              onChange={(e) => setVoice(e.currentTarget.value)}
              data={voices.map((v) => ({ value: v.id, label: t('ai.speech.voiceOption', { name: v.name, language: v.language_name }) }))}
              disabled={voices.length === 0}
              description={voicesQ.isError ? t('ai.speech.voicesFailed') : undefined}
            />
            <Select
              label={t('ai.speech.format')}
              value={format}
              onChange={(e) => setFormat(e.currentTarget.value as 'mp3' | 'ogg')}
              data={[{ value: 'mp3', label: t('ai.speech.format.mp3') }, { value: 'ogg', label: t('ai.speech.format.ogg') }]}
            />
            <ModelSelect kind="speech" value={chosen?.code ?? ''} onChange={setModel} />
            <Switch label={t('ai.unit.save')} description={t('ai.unit.saveHint')} checked={save} onChange={setSave} />
            <Group justify="flex-end">
              <Button type="submit" loading={run.isPending} disabled={!ready} data-testid="ai-speech-run">{t('ai.speech.run')}</Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.chargeNote')}</Text>
          </Stack>
        </form>
      </Card>
      <AiError error={run.error} />
      {result && src && (
        <Card withBorder>
          <Stack gap="xs" data-testid="ai-result">
            {result.stub && <Text role="bodySmall">{t('ai.result.stub')}</Text>}
            <audio controls src={src} className="rr-ai__audio" />
            <UnitCost minor={result.charged_minor} saved={!!result.media_id}>
              <Group gap="xs">
                {result.url && <a href={result.url} target="_blank" rel="noreferrer">{t('ai.unit.open')}</a>}
                <a href={src} download={`speech.${format}`}>{t('ai.unit.download')}</a>
              </Group>
            </UnitCost>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.speech.characters', { count: formatCount(result.characters) })}</Text>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
